import { useState, useEffect } from "react";
import PlatformBrand from "../components/PlatformBrand";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ResellerCodeField } from "../components/ResellerCodeField";
import { selectionFromQuery, computeQuote, money, presetFor } from "../lib/pricing";
import { featureLabel } from "../lib/features";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sun, Moon, Buildings, Rocket, Lightning, Crown, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import API from "../lib/api";

const PLAN_STYLES = [
  { icon: Rocket, color: "border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20", accent: "text-blue-600" },
  { icon: Lightning, color: "border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-950/20", accent: "text-purple-600" },
  { icon: Crown, color: "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20", accent: "text-amber-600" },
  { icon: ShieldCheck, color: "border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20", accent: "text-emerald-600" },
];
const CUSTOM_STYLE = { icon: CheckCircle, color: "border-teal-300 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-950/20", accent: "text-teal-600" };

export default function SignupPage() {
  const { formatApiError } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { search } = useLocation();
  const [resellerCode, setResellerCode] = useState(() => (new URLSearchParams(search).get("code") || new URLSearchParams(search).get("ref") || "").toUpperCase());
  const [resellerInfo, setResellerInfo] = useState(null);
  const onResellerValidated = (info) => {
    setResellerInfo(info);
    if (!info?.valid) return;
    const key = `fixlr_signup_start_${info.code}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    API.post(`/public/reseller-codes/${encodeURIComponent(info.code)}/track`, { event: "signup_start" }).catch(() => {});
  };
  const [catalog, setCatalog] = useState(null);
  const [custom, setCustom] = useState(() => { const q = selectionFromQuery(search); return q && (q.presetId === "custom" || q.features) ? q : null; });
  const [plan, setPlan] = useState(() => selectionFromQuery(search)?.presetId || "starter");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get("/billing/catalog").then(({ data }) => {
      setCatalog(data);
      const q = selectionFromQuery(search);
      if (q && q.features) {
        const preset = presetFor(data, q.features);
        const seats = q.seats || preset?.included_seats || data.config.min_seats;
        setCustom({ ...q, seats });
        setPlan(preset && (!q.seats || q.seats === preset.included_seats) && q.presetId !== "custom" ? preset.id : "custom");
      } else if (!q?.presetId || !data.presets.some((p) => p.id === q.presetId)) {
        setPlan((data.presets.find((p) => p.is_default) || data.presets[0])?.id || "starter");
      }
    }).catch(() => {});
  }, [search]);

  const presetCards = (catalog?.presets || []).map((p, i) => ({ ...p, ...PLAN_STYLES[i % PLAN_STYLES.length] }));
  const selectedPreset = presetCards.find((p) => p.id === plan);
  const customQuote = custom && catalog ? computeQuote(catalog, { seats: custom.seats, features: custom.features, cycle: custom.cycle }) : null;
  const summary = plan === "custom" && customQuote
    ? `Custom plan · ${customQuote.seats} user${customQuote.seats === 1 ? "" : "s"} · ${customQuote.features.length} add-on${customQuote.features.length === 1 ? "" : "s"} · ${money(customQuote.monthly_total)}/mo after trial`
    : selectedPreset ? `${selectedPreset.name} bundle · ${custom?.seats && plan === selectedPreset.id ? custom.seats : selectedPreset.included_seats} users · ${selectedPreset.trial_days}-day trial` : `${plan.replace("_", " ")} plan · 7-day trial`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (step === 1) {
      if (!companyName.trim() || !name.trim()) {
        setError("Company name and your name are required");
        return;
      }
      setStep(2);
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post("/auth/tenant-signup", {
        company_name: companyName,
        name,
        email,
        password,
        plan,
        seats: plan === "custom" ? customQuote?.seats : (custom?.seats && plan === custom.presetId ? custom.seats : selectedPreset?.included_seats),
        features: plan === "custom" ? customQuote?.features : selectedPreset?.feature_keys,
        billing_cycle: custom?.cycle || "monthly",
        reseller_code: resellerCode.trim() || undefined,
      });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      // Redirect to setup wizard
      navigate("/setup");
      window.location.reload();
    } catch (err) {
      setError(
        err.response?.data?.detail
          ? formatApiError(err.response.data.detail)
          : "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === "dark";

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center login-panel-dark">
        <div className="relative z-10 px-16 text-white">
          <PlatformBrand variant="login-hero" className="mb-8" />
          <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight mb-4 leading-tight">
            Start Managing<br />Your Properties
          </h1>
          <p className="text-base leading-relaxed max-w-md text-slate-300">
            Launch your own branded facilities management platform in minutes. No setup fees, 7-day free trial on all plans.
          </p>
          <div className="mt-12 space-y-3">
            {["White-label branding", "Unlimited customisation", "7-day free trial", "No credit card required"].map((item, i) => (
              <div key={item} className="flex items-center gap-2.5 text-slate-400">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="login-hex-grid" />
        <div className="login-glow login-glow-orange" />
        <div className="login-glow login-glow-blue" />
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <PlatformBrand variant="login-right" />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="signup-theme-toggle">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? "bg-primary" : "bg-muted"}`} />
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
          </div>

          <h2 className="text-2xl font-bold font-[Chivo] tracking-tight mb-1">
            {step === 1 ? "Create Your Workspace" : "Account Details"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {step === 1 ? "Tell us about your company" : "Set up your admin account"}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="signup-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 1 ? (
              <>
                <div>
                  <Label htmlFor="companyName">Company Name</Label>
                  <div className="relative mt-1">
                    <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="companyName"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Acme Property Services"
                      className="pl-9"
                      required
                      data-testid="signup-company-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="adminName">Your Name</Label>
                  <Input
                    id="adminName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    className="mt-1"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Select a Plan</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="signup-plan-grid">
                    {presetCards.map((p) => {
                      const Icon = p.icon;
                      const selected = plan === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPlan(p.id)}
                          className={`border-2 rounded-lg p-3 text-left transition-all ${selected ? `${p.color} ring-2 ring-primary/30` : "border-border bg-card hover:border-muted-foreground/30"}`}
                          data-testid={`signup-plan-${p.id}`}
                        >
                          <Icon size={16} className={p.accent} weight="fill" />
                          <p className="text-xs font-bold mt-1.5">{p.name}</p>
                          <p className="text-[10px] text-muted-foreground">{money(p.monthly_total, { compact: true })}/mo · {p.included_seats} users</p>
                          <p className="text-[9px] text-muted-foreground mt-0.5">{p.feature_keys.length} add-on{p.feature_keys.length === 1 ? "" : "s"}{p.bundle_discount_pct > 0 ? ` · save ${p.bundle_discount_pct}%` : ""}</p>
                        </button>
                      );
                    })}
                    {customQuote && (
                      <button type="button" onClick={() => setPlan("custom")}
                        className={`border-2 rounded-lg p-3 text-left transition-all ${plan === "custom" ? `${CUSTOM_STYLE.color} ring-2 ring-primary/30` : "border-border bg-card hover:border-muted-foreground/30"}`}
                        data-testid="signup-plan-custom">
                        <CheckCircle size={16} className={CUSTOM_STYLE.accent} weight="fill" />
                        <p className="text-xs font-bold mt-1.5">Your custom plan</p>
                        <p className="text-[10px] text-muted-foreground">{money(customQuote.monthly_total, { compact: true })}/mo · {customQuote.seats} users</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{customQuote.features.length ? customQuote.features.map(featureLabel).join(", ") : "Core modules only"}</p>
                      </button>
                    )}
                    {!catalog && <p className="col-span-2 text-[10px] text-muted-foreground">Loading plans…</p>}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Want a different mix? <Link to="/product/pricing#build" className="text-primary hover:underline" data-testid="signup-build-plan-link">Build your own plan</Link> — the trial runs on exactly what you pick.</p>
                </div>
                <Button type="submit" className="w-full" data-testid="signup-next-btn">
                  Continue
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="signupEmail">Email Address</Label>
                  <Input
                    id="signupEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="signupPassword">Password</Label>
                  <Input
                    id="signupPassword"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="mt-1"
                    required
                    minLength={8}
                    data-testid="signup-password-input"
                  />
                </div>
                <ResellerCodeField value={resellerCode} onChange={setResellerCode} onValidated={onResellerValidated} testId="signup-reseller-code" />
                <div className="border border-border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold">{companyName}</p>
                      <p className="text-[10px] text-muted-foreground" data-testid="signup-plan-summary">{summary}{resellerInfo?.valid ? ` · ${resellerInfo.discount_pct}% partner discount applied` : ""}</p>
                    </div>
                    <button type="button" onClick={() => setStep(1)} className="text-[10px] text-primary hover:underline" data-testid="signup-back-btn">
                      Change
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
                  {loading ? "Creating workspace..." : "Create Workspace"}
                </Button>
              </>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
